import {
  Button,
  Dropdown,
  MenuProps,
  Space,
  Table,
  Typography,
  message,
} from "antd";
import {
  useActivateStoresMutation,
  useDeactivateStoresMutation,
  useDeleteStoreMutation,
  useGetAllMyStoresQuery,
  useStoreCreatedSubscription,
  useStoreUpdatedSubscription,
} from "../graphql/generated/hooks";
import { useTranslation } from "react-i18next";
import RobinPage from "../components/core/RobinPage";
import EditDeleteActions from "../components/EditDeleteActions";
import { useNavigate } from "react-router-dom";
import { faRotateRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import {
  CheckOutlined,
  DeleteOutlined,
  DownOutlined,
  StopOutlined,
} from "@ant-design/icons";

function StoreList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const { data: stores, loading, refetch } = useGetAllMyStoresQuery();
  const [deleteStore, { loading: deleting }] = useDeleteStoreMutation();
  const [activateStores] = useActivateStoresMutation();
  const [deactivateStores] = useDeactivateStoresMutation();

  const { data: storeCreated } = useStoreCreatedSubscription();
  const { data: storeUpdated } = useStoreUpdatedSubscription();


  const [checkedList, setCheckedList] = useState<number[]>([]);

  const dataSource = stores?.getAllStoresForPartner?.map((store) => ({
    key: store.id,
    name: store.name,
    nameAr: store.nameAr,
    phone: store.phone,
    address: store.address,
    isActive: store.isActive,
    defaultWorkingHours: store.defaultWorkingHours?.name,
    actions: (
      <EditDeleteActions
        onEdit={() => {
          navigate(`/settings/stores/mutate/${store.id}`, {
            state: {
              store: {
                ...store,
                defaultWorkingHoursId: store.defaultWorkingHours?.id,
              },
            },
          });
        }}
        onDelete={() => {
          deleteStore({ variables: { id: store.id } })
            .then(() => {
              messageApi.success(t("message.success.data_deleted"));
              refetch();
            })
            .catch((error) => {
              messageApi.error(t("message.error.data_delete_failed"));
            });
        }}
        loadingDelete={deleting}
      />
    ),
  }));

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Name Ar",
      dataIndex: "nameAr",
      key: "nameAr",
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
    },
    {
      title: "Default Working Hours",
      dataIndex: "defaultWorkingHours",
      key: "defaultWorkingHours",
    },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive: boolean) => (isActive ? "Yes" : "No"),
    },
    { title: "Actions", dataIndex: "actions", key: "actions" },
  ];

  const items: MenuProps["items"] = [
    {
      label: t("button.activate"),
      key: "1",
      icon: <CheckOutlined />,
      onClick: () => {
        activateStores({ variables: { ids: checkedList } })
          .then(() => {
            messageApi.success(t("message.success.data_updated"));
            refetch();
          })
          .catch((error) => {
            messageApi.error(t("message.error.data_update_failed"));
          });

        setCheckedList([]);
      },
    },
    {
      label: t("button.deactivate"),
      key: "2",
      icon: <StopOutlined />,
      onClick: () => {
        deactivateStores({ variables: { ids: checkedList } })
          .then(() => {
            messageApi.success(t("message.success.data_updated"));
            refetch();
          })
          .catch((error) => {
            messageApi.error(t("message.error.data_update_failed"));
          });

        setCheckedList([]);
      },
    },
    {
      label: t("button.delete"),
      key: "3",
      danger: true,
      icon: <DeleteOutlined />,
      onClick: () => {
        checkedList.map((id) => {
          return deleteStore({ variables: { id } })
            .then(() => {
              messageApi.success(t("message.success.data_deleted"));
              refetch();
            })
            .catch((error) => {
              messageApi.error(t("message.error.data_delete_failed"));
            });
        });
        setCheckedList([]);
      },
    },
  ];

  return (
    <RobinPage
      title={t("title.store_settings")}
      actions={[
        {
          label: (
            <Typography.Link
              onClick={() => {
                refetch();
              }}
            >
              <FontAwesomeIcon icon={faRotateRight} />
            </Typography.Link>
          ),
        },
        {
          label: (
            <Button
              onClick={() => {
                navigate("/settings/stores/mutate");
              }}
            >
              {t("button.add")}
            </Button>
          ),
        },
        {
          label: checkedList.length ? (
            <Dropdown
              menu={{
                items,
              }}
            >
              <Button>
                <Space>
                  {t("button.actions")}
                  <DownOutlined />
                </Space>
              </Button>
            </Dropdown>
          ) : (
            <></>
          ),
        },
      ]}
    >
      {contextHolder}
      <Table
        rowSelection={{
          selectedRowKeys: checkedList,
          type: "checkbox",
          onChange: (selectedRowKeys: React.Key[]) => {
            setCheckedList(selectedRowKeys.map((key) => Number(key)));
          },
        }}
        loading={loading}
        dataSource={dataSource}
        columns={columns}
      />
    </RobinPage>
  );
}

export default StoreList;
